/**
 * useVisibilityScope — builds the visibility scope for CRM list queries.
 *
 * Drop-in evolution of the old per-page expression
 *   ownerId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid
 * Pages now write
 *   const { scope } = useVisibilityScope({
 *     entityType: 'contact' | 'deal',
 *     targetUserId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid,
 *   });
 * and pass `ownerScope: scope` to useContactsTable / useDealsTable.
 *
 * Semantics:
 *   - targetUserId null + viewer is CRM admin → { mode: 'all' } (All Users)
 *   - otherwise → the target user's EFFECTIVE book: records they own, books
 *     shared with them via teams, records shared with them individually, and
 *     (for admins) company records.
 *
 * The scope starts as exactly the target's own records (synchronous, so the
 *   first render is never wider than the legacy single-owner behavior) and
 *   widens once the accessible owners resolve from crm_accessible_owner_ids().
 *
 * Live updates:
 *   - crm_team_members (member_uid=eq.target) → refetch accessible owners
 *   - crm_record_shares (user_id=eq.target) → bump grantsVersion inside the
 *     scope object, which re-keys downstream fetch effects so lists refetch
 *     when a record-level share is granted/revoked.
 */

import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { useAuth } from '../contexts/AuthContext';
import { getSupabase, isSupabaseConfigured } from '../utils/supabase';
import {
  buildVisibilityScope,
  fetchAccessibleOwnerIds,
  visibilityScopeKey,
} from '../utils/recordVisibility';

export function useVisibilityScope({
  entityType,
  targetUserId = null,
  targetIsAdmin = undefined,
} = {}) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const viewerIsAdmin = SuperAdmin || Roles?.includes('CRM Admin') || false;
  const viewerUid = currentUser?.uid || null;

  const [ownerIds, setOwnerIds] = useState(null);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [grantsVersion, setGrantsVersion] = useState(0);
  const [teamVersion, setTeamVersion] = useState(0);
  const [fetchedTargetIsAdmin, setFetchedTargetIsAdmin] = useState(undefined);

  // When an admin views ANOTHER user's book and the caller didn't say whether
  // that target is an admin, resolve it from their user doc (controls whether
  // company records appear in the target's book). Own-book and explicit-prop
  // cases need no fetch; starts undefined → treated as non-admin until known,
  // so the scope is never transiently wider than it should be.
  useEffect(() => {
    let cancelled = false;
    setFetchedTargetIsAdmin(undefined);
    if (targetIsAdmin !== undefined) return undefined;
    if (!targetUserId || targetUserId === viewerUid) return undefined;
    getDoc(doc(db, 'users', targetUserId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        const admin =
          data.SuperAdmin === true ||
          data.CRMRole === 'CRM Admin' ||
          (Array.isArray(data.Roles) && data.Roles.includes('CRM Admin'));
        setFetchedTargetIsAdmin(admin);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [targetUserId, viewerUid, targetIsAdmin]);

  const effectiveTargetIsAdmin =
    targetIsAdmin !== undefined ? targetIsAdmin : fetchedTargetIsAdmin;

  // Base scope, synchronous — identical to legacy behavior until owners load.
  const baseScope = useMemo(
    () =>
      buildVisibilityScope({
        targetUserId,
        viewerIsAdmin,
        viewerUid,
        targetIsAdmin: effectiveTargetIsAdmin,
      }),
    [targetUserId, viewerIsAdmin, viewerUid, effectiveTargetIsAdmin]
  );

  const scopedTargetUid =
    baseScope.mode === 'scoped' && baseScope.targetUid !== '__none__'
      ? baseScope.targetUid
      : null;

  // Resolve accessible owners (book shares) for the target.
  useEffect(() => {
    let cancelled = false;
    if (!scopedTargetUid) {
      setOwnerIds(null);
      return undefined;
    }
    setOwnersLoading(true);
    fetchAccessibleOwnerIds(scopedTargetUid, entityType)
      .then((ids) => {
        if (!cancelled) setOwnerIds(ids);
      })
      .finally(() => {
        if (!cancelled) setOwnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopedTargetUid, entityType, teamVersion]);

  // Live grant-change subscriptions (small tables; REPLICA IDENTITY FULL so
  // filtered DELETEs arrive too).
  useEffect(() => {
    if (!scopedTargetUid || !isSupabaseConfigured()) return undefined;
    const supabase = getSupabase();

    const channel = supabase
      .channel(`visibility-scope-${scopedTargetUid}-${entityType}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_team_members',
          filter: `member_uid=eq.${scopedTargetUid}`,
        },
        () => setTeamVersion((v) => v + 1)
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_record_shares',
          filter: `user_id=eq.${scopedTargetUid}`,
        },
        () => setGrantsVersion((v) => v + 1)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [scopedTargetUid, entityType]);

  const scope = useMemo(() => {
    if (baseScope.mode !== 'scoped') return baseScope;
    return {
      ...baseScope,
      ownerIds:
        Array.isArray(ownerIds) && ownerIds.length > 0 ? ownerIds : baseScope.ownerIds,
      grantsVersion,
    };
  }, [baseScope, ownerIds, grantsVersion]);

  const scopeKey = useMemo(
    () => `${visibilityScopeKey(scope)}:g${grantsVersion}`,
    [scope, grantsVersion]
  );

  return {
    scope,
    scopeKey,
    loading: ownersLoading,
    viewerIsAdmin,
  };
}

export default useVisibilityScope;
